import { DefaultDirection } from "src/shared/models/metric";
import { GameObject } from "src/app/dashboard/modules/cxone-games/game.models";
import { GameType } from "./games.model";
import { getStandardizeRewards } from "./games-parameter-history";
import { IHGameParams, IHSelectedMetric, MetricValues, ParticipantObj, ManualResult, StandardizeRewards, RankingParticipant, ActionHistory, HistoricalTeamObj, IHGameStandardize, HTournamentInfo, MetricDetail } from './games-parameter.model';

const INTERVAL_TYPE = {
    DAY: 0,
    WEEK: 1,
    MONTH: 2
} as const;

// local functions
const defaultLocalization = (key: string, defaultText?: string) => defaultText

// Participants: Game history
// Pure function section
export const isConnect3 = (gameType: number) => gameType === GameType.Connect3;
export const isConnectAll = (gameType: number) => gameType === GameType.Blackout;
export const isTopTeam = (gameType: number) => gameType === GameType.TopTeam;
export const isTopDog = (gameType: number) => gameType === GameType.TopDog;
export const isMilestone = (gameType: number) => gameType === GameType.Milestone;
export const isRaffle = (gameType: number) => gameType === GameType.Raffle;
export const isBracket = (gameType: number) => gameType === GameType.Brackets;
export const isBullseye = (gameType: number) => gameType === GameType.Bullseye;

export const getCenterClass = (isCXOne: boolean): string => isCXOne ? 'd-flex align-items-center justify-content-center h-100' : '';
export const getHeaderClass = (isCXOne: boolean): string => isCXOne ? 'player-breakdown__grid-header' : '';
export const getCellClass = (isCXOne: boolean): string => isCXOne ? 'player-breakdown__grid-cell' : '';
export const getRowPinnedClass = (isCXOne: boolean, isRowPinned: string | undefined | null): string => isCXOne && isRowPinned ? 'player-breakdown__grid-row-pinned d-flex align-items-center' : '';
export const getStatusIcon = (
    type: 'checkMark' | 'close',
    platform: 'PM' | 'CXOne',
    extraClass: string = ''
): string => {
    if (type === 'checkMark') {
        return platform === 'PM'
            ? `<svg class="icon-16 fill-emeraldCity ${extraClass}"><use href="../App_Themes/clearview-default/icons/utility/checkmark.svg#checkmark"></use></svg>`
            : `<svg class="checkmark-icon ${extraClass}" style="width: 14.03px !important;height: 11.43px !important;"><use href="../App_Themes/CXone/icons/gamification/checkmark.svg#checkmark"></use></svg>`;
    }
    if (type === 'close') {
        return platform === 'PM'
            ? `<svg class="icon-16 fill-redRyder ${extraClass}"><use href="../App_Themes/clearview-default/icons/utility/close.svg#close"></use></svg>`
            : `<svg class="close-icon ${extraClass}" style="width: 16.87px !important;height: 16px !important;"><use href="../App_Themes/CXone/icons/gamification/close.svg#close"></use></svg>`;
    }
    return '';
};
export const getBadgeSize = (isCXOne: boolean): number => isCXOne ? 24 : 26;
export const getHeaderCenterClass = (isCXOne: boolean): string => isCXOne ? 'justify-content-center' : '';

export const getAllMetrics = (game: IHGameParams): IHSelectedMetric[] => {
    if (!game?.HistorySelectedMetrics) return [];
    return game.HistorySelectedMetrics.map(s => {
        if (game.HMetricDetails[s.MetricID]) return {
            ...s,
            MetricDetail: game.HMetricDetails[s.MetricID],
        };
        return s;
    });
};

export const getSingleMetric = (game: IHGameParams) => {
    const allMetrics = getAllMetrics(game);
    const metric = game.GameType === GameType.Raffle
        ? allMetrics[0]
        : allMetrics.filter(m => !m.IsQualifier)[0]; // taking the first metric that is not a qualifier
    metric.IsQualifier = false;
    return metric;
};

export const getConnect3Metrics = (game: IHGameParams): IHSelectedMetric[] => {
    if (!game?.HistorySelectedMetrics) return [];
    const metrics = game.HistorySelectedMetrics.filter(m => !m.IsQualifier).map(s => {
        if (game.HMetricDetails[s.MetricID]) return {
            ...s,
            MetricDetail: game.HMetricDetails[s.MetricID],
        };
        return s;
    });
    if (metrics.length < 9) {
        /**
         * 0,1,2
         * 3,4,5 -> free space is always 4
         * 6,7,8
         */
        metrics.splice(4, 0, {
            HistoryID: 0,
            HSelectedMetricsID: 0,
            IsQualifier: false,
            LeftNumber: null,
            MetricID: -1,
            RightNumber: -1,
            Position: 4,
            MetricDetail: {
                Abbrev: 'Free_Space',
                MetricID: -1,
                Name: 'Free_Space',
                ConversionType: 0,
                DefaultMetricDirection: 0
            }
        });
    }
    metrics.sort((a, b) => {
        return a.Position - b.Position;
    });
    return metrics;
};

export const getQualifier = (game: IHGameParams): IHSelectedMetric | undefined => {
    const allMetrics = getAllMetrics(game);
    const qualifiers = allMetrics.filter(m => m.IsQualifier);
    if (isRaffle(game.GameType)) {
        return qualifiers.length === 2 && qualifiers[1];
    }
    return qualifiers[qualifiers.length - 1];
};

export const hasReward = (participant: ParticipantObj) => {
    return participant.calculatedCoins > 0 || participant.calculatedXP > 0 || participant.custom?.length > 0 || participant.badges?.length > 0;
}

export const isQualified = (qualifierMetric: IHSelectedMetric, myValue: number) => {
    let isQualified = false;

    switch (qualifierMetric.ComparerType) {
        case 1: // Greater than
            isQualified = myValue > qualifierMetric.LeftNumber;
            break;
        case 2: // Less than
            isQualified = myValue < qualifierMetric.LeftNumber;
            break;
        case 3: // Between
            // Check if the left number is greater than the right number
            if (qualifierMetric.LeftNumber > qualifierMetric.RightNumber) {
                isQualified = myValue >= qualifierMetric.RightNumber && myValue <= qualifierMetric.LeftNumber;
            } else {
                isQualified = myValue >= qualifierMetric.LeftNumber && 
                             myValue <= (qualifierMetric.RightNumber ?? qualifierMetric.LeftNumber);
            }
            break;
        default:
            return false;
    }
    return isQualified;
};

export const getBracketRanking = (game: IHGameStandardize, actionHistory: ActionHistory[], info: HTournamentInfo): ManualResult  => {
    const manualRank: ManualResult = {
        shouldCalculateResult: true,
        ranking: [],
    };
    if (!info.matches.leftSide || !info.matches.rightSide || !info.NumberOfRounds) return manualRank;
    const validRounds = Math.min(info.NumberOfRounds, 4); // final, semi final, quarters and octaves
    const sortValues = <T extends { place?: number }>(a: T, b: T) => {
        return a.place - b.place;
    };

    const allParticipants: RankingParticipant[] = [...info.matches.leftSide.flat(5), ...info.matches.rightSide.flat(5)]
        .filter(m => m.isWinner && m.RoundNumber <= validRounds)
        .map(m => ({
            RelatedID: m.LoginID,
            GroupID: m.GroupID,
            place: m.RoundNumber
        }));
    allParticipants.sort(sortValues);
    return {
        ...manualRank,
        ranking: allParticipants,
    };
};

export const getRanking = (participants: ParticipantObj[], game: IHGameStandardize, actionHistory: ActionHistory[], teamsInfo: { [key: number]: HistoricalTeamObj } = {}) : ManualResult => {
    const metric = game.metricDefinition;
    const currentDate = new Date();
    currentDate.setHours(0,0,0,0);
    switch(game.IntervalType) {
        case INTERVAL_TYPE.DAY:
            currentDate.addDays(-1 * game.IntervalAmount + 1);
            break;
        case INTERVAL_TYPE.WEEK:
            currentDate.addDays(game.IntervalAmount * -7 + 1);
            break;
        case INTERVAL_TYPE.MONTH:
            currentDate.addMonths(-1 * game.IntervalAmount).addDays(1);
    }
    const firstParticipant = participants[0];
    const direction = metric?.MetricDetail?.DefaultMetricDirection;
    const manualRank: ManualResult = {
        shouldCalculateResult: actionHistory.length === 0 && firstParticipant?.participant?.PeriodStartDate && new Date(firstParticipant.participant.PeriodStartDate) >= currentDate, // ranking users manually only if there is no actionHistory
        ranking: [],
    }; 

    const sortValues = <T extends { metricValue?: number }>(a: T, b: T) => {
        if (direction) return b.metricValue - a.metricValue;
        return a.metricValue - b.metricValue;
    };

    if (metric?.MetricID > 0 && isTopDog(game.GameType)) {
        const participantsSorts: ParticipantObj[] = participants.flatMap(p => {
            const metricValue = p?.metricsValues?.find(m => m.MetricID === metric.MetricID)
            if (metricValue?.MetricDisplayValue != null && metricValue.MetricValue !== null) {
                return {
                    ...p,
                    metricValue: metricValue.MetricValue,
                };
            }
            return [];
        });
        participantsSorts.sort(sortValues);
        const ranking: RankingParticipant[] = [];
        let currentPlace = 0;
        let previousMetric = null;
        participantsSorts.forEach(p => {
            if (p.metricValue != previousMetric) {
                currentPlace = currentPlace + 1;
            }
            ranking.push({
                RelatedID: p.participant.LoginID,
                place: currentPlace
            });
            previousMetric = p.metricValue;
        });
        const totalRewards = getStandardizeRewards(game).length;
        manualRank.ranking = ranking.filter(r => r.place <= totalRewards);
    }
    if (metric?.MetricID > 0 && isTopTeam(game.GameType)) {
        const teamsSorts: (HistoricalTeamObj & { metricValue?: number })[] = Object.values(teamsInfo).flatMap(t => {
            const metricValue = t.metricsValues?.find(m => m.MetricID === metric.MetricID)
            if (metricValue?.MetricDisplayValue != null && metricValue.MetricValue !== null) {
                return {
                    ...t,
                    metricValue: metricValue.MetricValue,
                };
            }
            return [];
        });
        teamsSorts.sort(sortValues);
        const ranking: RankingParticipant[] = [];
        let currentPlace = 0;
        let previousMetric = null;
        teamsSorts.forEach(t => {
            if (t.metricValue != previousMetric) {
                currentPlace = currentPlace + 1;
            }
            ranking.push({
                RelatedID: t.TeamID,
                place: currentPlace
            });
            previousMetric = t.metricValue;
        });
        const totalRewards = game?.rewards?.length || 0;
        manualRank.ranking = ranking.filter(r => r.place <= totalRewards);
    }    
    return manualRank;
};

// Results functions

/**
 * Connect 3 and connect all values
 * 2^0   2^1   2^2
 * 2^3   2^4   2^5
 * 2^6   2^7   2^8
 * 
 * win cases connect 3
 * 2^0 + 2^1 + 2^2 = 7
 * 2^3 + 2^4 + 2^4 = 56
 * 2^5 + 2^6 + 2^7 = 448
 * 2^0 + 2^3 + 2^6 = 73
 * 2^1 + 2^4 + 2^7 = 146
 * 2^2 + 2^5 + 2^8 = 292
 * 2^2 + 2^4 + 2^6 = 84
 * 2^0 + 2^4 + 2^8 = 273
 * 
 * win cases connect all
 * 2^0 + 2^1 + 2^2 = 7 +
 * 2^3 + 2^4 + 2^4 = 56 +
 * 2^5 + 2^6 + 2^7 = 448 =
 *                   511
 */
const WIN_CASES_CONNECT3 = [7, 56, 448, 73, 146, 292, 84, 273] as const;
const WIN_CASES_BLACKOUT = [511] as const;

const hasRewardPlace = (participant: ParticipantObj, reward: StandardizeRewards) => {
    const coins = reward.coins || 0;
    const xp = reward.xp || 0;
    const badges = reward.badges || 0;
    return Math.abs(participant.calculatedCoins) == coins && Math.abs(participant.calculatedXP) == xp && (!badges && !participant.badges?.length || !!participant.badges?.find(b => b === badges));
};

const meetsMetricCriteria = (ComparisonType: number, value: number, minValue?: number, maxValue?: number): boolean => {
    const ComparerType = {
        1: (): boolean => value >= minValue,
        2: (): boolean => value <= minValue,
        3: (): boolean => value >= minValue && value <= maxValue
    };
    return ComparerType[ComparisonType] && ComparerType[ComparisonType]() || false;
}

const getResultValueForRaffle = (participantObject: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization): string => {
    if (hasReward(participantObject)) return localizationFn('Winner', 'Winner');
    if (!participantObject.passed) return localizationFn("Disqualified", "Disqualified");
    if (!participantObject.metric) return localizationFn('No_Results', 'No results');
    const metric = getSingleMetric(game);
    if (participantObject.passed && metric?.ComparerType && meetsMetricCriteria(metric.ComparerType, participantObject.metricValue, metric.LeftNumber, metric.RightNumber))
        return localizationFn('Raffle_Participant', 'Raffle Participant');

    return !Number.isNaN(participantObject.metricValue) ? localizationFn('No_Ticket', 'No Ticket') : localizationFn('No_Results', 'No results');
};

const getResultForMultiplier = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization) => {
    if (hasReward(participant)) {
        return localizationFn("Winner", "Winner");
    };
    return localizationFn("No_Results", "No Results");
};

const winnerRankWrapper = (manualrank: ManualResult) => {
    
    const isWinnerRank = (participantObj: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            const participantRank = ranking.find(r => r.RelatedID == participantObj?.participant?.LoginID);
            if (participantRank && participantRank.RelatedID > 0) {
                return getLocalizedTierText(participantRank.place, game.GameType, localizationFn);
            }
        }

        if (!hasReward(participantObj)) {
            return localizationFn("No_Results", "No Results");
        }
    
        const rewards = getStandardizeRewards(game).sort((a, b) => a.position - b.position);
        let result = '';
    
        rewards.forEach((reward, index) => {
            const isWinner = hasRewardPlace(participantObj, reward);
            if (isWinner && !result) {
                participantObj.rank = index + 1;
                result = getLocalizedTierText(index + 1, game.GameType, localizationFn);
            }
        });
    
        return result || localizationFn("No_Results", "No Results");
    }
    return isWinnerRank;
};

const winnerRankWrapperTopTeam = (manualrank: ManualResult) => {

    const isWinnerRank = (participantObj: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            const participantRank = ranking.find(r => r.RelatedID == participantObj?.participant?.TeamID);
            if (participantRank && participantRank.RelatedID > 0) {
                return getLocalizedTierText(participantRank.place, game.GameType, localizationFn);
            }
        }

        if (!hasReward(participantObj)) {
            return localizationFn("No_Results", "No Results");
        }
    
        const rewards = getStandardizeRewards(game).sort((a, b) => a.position - b.position);
        let result = '';
    
        rewards.forEach((reward, index) => {
            const isWinner = hasRewardPlace(participantObj, reward);
            if (isWinner && !result) {
                participantObj.rank = index + 1;
                result = getLocalizedTierText(index + 1, game.GameType, localizationFn);
                participantObj.results = result;
            }
        });
    
        return result || localizationFn("No_Results", "No Results");
    }
    return isWinnerRank;
};

export const isWinnerConnect3Bool = (participant: ParticipantObj) => {
    const metricCompleted = participant.connect3Metrics;
    if (!metricCompleted) return false;

    let bin = 0;
    for (let i = 0; i < metricCompleted.length; i++) {
        bin += (metricCompleted[i].metricCompleted) ? Math.pow(2, i) : 0;
    }

    for (let i = 0; i < WIN_CASES_CONNECT3.length; i++) {
        let result = bin & WIN_CASES_CONNECT3[i];
        if (result === WIN_CASES_CONNECT3[i])
            return true;
    }
    return false;
}

export const isWinnerConnect3 = (participant: ParticipantObj, _: IHGameParams, localizationFn = defaultLocalization) => {
    if (!participant.connect3Metrics) return localizationFn("No_Connections", "No Connections");
    return isWinnerConnect3Bool(participant)
        ? localizationFn("Connect3", "Connect 3")
        : localizationFn("No_Connections", "No Connections");
};

export const isWinnerConnect3Status = (participant: ParticipantObj): number => {
    return isWinnerConnect3Bool(participant) ? 1 : 0;
};

const isWinnerBlackoutBool = (participant: ParticipantObj) => {
    const metricCompleted = participant.connect3Metrics;
    if (!metricCompleted) return false;
    let bin = 0;
    metricCompleted.forEach((m, i) => {
        bin += (m?.metricCompleted) ? Math.pow(2, i) : 0;
    });

    for (let i = 0; i < WIN_CASES_BLACKOUT.length; i++) {
        const result = bin & WIN_CASES_BLACKOUT[i];
        if (result === WIN_CASES_BLACKOUT[i])
            return true;
    }
    return false;
}

export const isWinnerBlackout = (participant: ParticipantObj, _: IHGameParams, localizationFn = defaultLocalization) => {
    if (!participant.connect3Metrics) return localizationFn("Not_All_Connections", "Not All Connections");
    return isWinnerBlackoutBool(participant)
        ? localizationFn("Connected_All", "Connected All")
        : localizationFn("Not_All_Connections", "Not All Connections");
};

export const isWinnerBlackoutStatus = (participant: ParticipantObj): number => {
    return isWinnerBlackoutBool(participant) ? 1 : 0;
};

const getCalculateLevelValue = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, isCXOne = false) => {
    const metrics = getAllMetrics(game).filter(m => !m.IsQualifier);
    if (isMilestone(game.GameType)) metrics.reverse();// start with the last milestone saved 
    const metricValue = participant.metricValue;
    let metCriteriaResult = '';
    metrics.forEach((metric, index) => {
        if (metric.ComparerType && !metCriteriaResult) {
            const meetsCriteria = meetsMetricCriteria(metric.ComparerType, metricValue, metric.LeftNumber, metric.RightNumber);
            if (meetsCriteria) {
                metCriteriaResult = getLocalizedTierText(index + 1, game.GameType, localizationFn, isCXOne);
            }
        }
    });
    return metCriteriaResult || localizationFn("No_Results", "No Results");
}

const isWinnerLevel = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false, isCXOne = false) => {
    if (shouldCalculateResult) {
        return getCalculateLevelValue(participant, game, localizationFn, isCXOne);
    }

    if (!hasReward(participant)) return localizationFn("No_Results", "No Results");

    const rewards = getStandardizeRewards(game);
    if (!isMilestone(game.GameType)) rewards.sort((a, b) => a.position - b.position);
    let result = '';

    if (!result) rewards.forEach((reward, index) => {
        const isWinnerPrize = hasRewardPlace(participant, reward);
        if (isWinnerPrize && !result) {
            result = getLocalizedTierText(index + 1, game.GameType, localizationFn, isCXOne);
        }
    });

    return result || getCalculateLevelValue(participant, game, localizationFn, isCXOne);
}

export const isWinnerLevelBool = (
    participant: ParticipantObj,
    game: IHGameParams,
    localizationFn = defaultLocalization,
    shouldCalculateResult = false,
    isCXOne = false
): number => {
    const result = isWinnerLevel(participant, game, localizationFn, shouldCalculateResult, isCXOne);

    // If the result is not 'No Results' or 'No_Results', then it wins
    const noResultsText = localizationFn("No_Results", "No Results");
    return result !== noResultsText && result !== '' ? 1 : 0;
};

const getLocalizedRoundText = (stage: number, localizationFn = defaultLocalization) => {
    switch (stage) {
        case 1:
            return localizationFn("Round_1_Final", "Final");
        case 2:
            return localizationFn("Round_2_Semi_Finals", "Semi Finals");
        case 3:
            return localizationFn("Round_3_Quarter", "Quarters");
        case 4:
            return localizationFn("Round_4_Octaves", "Octaves");
        default:
            return localizationFn("No_Wins", "No Wins");
    }
}

const wrapperTournament = (manualrank: ManualResult) => {

    const isWinnerBracketTournament = (participant: ParticipantObj, game: IHGameParams, localizationFn = defaultLocalization, shouldCalculateResult = false) => {
        const ranking = manualrank.ranking;
        if (shouldCalculateResult) {
            // Match by LoginID; fall back to GroupID when the bracket row's LoginID lookup didn't resolve (returns 0)
            const participantRank = ranking.find(r =>
                (r.RelatedID > 0 && r.RelatedID == participant?.participant?.LoginID) ||
                (r.GroupID && r.GroupID == participant?.participant?.RelatedGroupID)
            );
            if (participantRank && (participantRank.RelatedID > 0 || participantRank.GroupID > 0)) {
                return getLocalizedRoundText(participantRank.place, localizationFn);
            } else if (participant?.metricValue === undefined || participant?.metricValue === null || !participant?.metricsValues) return localizationFn("No_Results", "No Results");
        }
        return localizationFn("No_Wins", "No Wins");
    }
    return isWinnerBracketTournament;
};


export const getBracketTournamentLastRound = (result: string, ranking: number, NumberOfRounds: number, localizationFn = defaultLocalization): number => {
    switch (result) {
        case localizationFn("Round_1_Final", "Final"):
        case localizationFn("Round_2_Semi_Finals", "Semi Finals"):
            return 1;
        case localizationFn("No_Wins", "No Wins"):
            return NumberOfRounds;
        default:
            return ranking - 1;
    }
}

// End results functions

export const getResultsValues = (
    participant: ParticipantObj,
    game: IHGameParams,
    localizationFn = defaultLocalization,
    manualResult: ManualResult = { ranking: [], shouldCalculateResult: false },
    isCxone = false
) => {
    type ResultsFn = {
        [gameType: number]: (p: ParticipantObj, g: IHGameParams, l: (key: string, defaultText?: string) => string, shouldCalculateResult: boolean) => string;
    }

    const isWinnerLevelWithCXOne = (participant: ParticipantObj, gameParams: IHGameParams, localization: (key: string, defaultText?: string) => string, shouldCalculateResult: boolean) => {
        return isWinnerLevel(participant, gameParams, localization, shouldCalculateResult, isCxone);
    };

    const rewardsFn: ResultsFn = {
        [GameType.MultiplierMadness]: getResultForMultiplier,
        [GameType.TopDog]: winnerRankWrapper(manualResult), // HOF, return the function that we need for ranks
        [GameType.Bullseye]: isWinnerLevelWithCXOne,
        [GameType.Milestone]: isWinnerLevelWithCXOne, //isWinnerMilestone,
        [GameType.Connect3]: isWinnerConnect3,
        [GameType.Blackout]: isWinnerBlackout,
        [GameType.TopTeam]: winnerRankWrapperTopTeam(manualResult),
        [GameType.Raffle]: getResultValueForRaffle,
        [GameType.Brackets]: wrapperTournament(manualResult),
    }
    return rewardsFn[game.GameType](participant, game, localizationFn, manualResult.shouldCalculateResult) || '';
}

const getLocalizedTierText = (tier: number, gameType: GameType, localizationFn = defaultLocalization, isCXOne = false) => {
    const tierOptions = {
        [GameType.TopDog]: {
            1: () => localizationFn("First_Place", "1st Place"),
            2: () => localizationFn("Second_Place", "2nd Place"),
            3: () => localizationFn("Third_Place", "3rd Place"),
        },
        [GameType.TopTeam]: {
            1: () => localizationFn("First_Place", "1st Place"),
            2: () => localizationFn("Second_Place", "2nd Place"),
            3: () => localizationFn("Third_Place", "3rd Place"),
        },
        [GameType.Bullseye]: {
            1: () => isCXOne ? localizationFn("Bullseye", "Bullseye") : localizationFn("Ring_Prize", "Bullseye"),
            2: () => localizationFn("2nd_Ring", "2nd Ring"),
            3: () => localizationFn("3rd_Ring", "3rd Ring"),
        },
        default: {
            [GameType.TopDog]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Place", "Place")}`,
            [GameType.TopTeam]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Place", "Place")}`,
            [GameType.Bullseye]: (tier: number) => `${tier}${localizationFn("Th_Symbol", "th")} ${localizationFn("Ring", "Ring")}`,
            [GameType.Milestone]: (tier: number) => `${localizationFn("Milestone", "Milestone")} ${tier}`,
        }
    };

    const tierValue: string = tierOptions[gameType] && tierOptions[gameType][tier] && tierOptions[gameType][tier]();
    return tierValue || tierOptions.default[gameType] && tierOptions.default[gameType](tier) || '';
};

// functions with side effect
export const setParticipantDefaultValues = (participant: ParticipantObj, gameType: number, localizationFn = (key: string, defaultText?: string) => defaultText) => {
    participant.metric = '---';
    participant.qualifier = '---';
    participant.passed = false;
    participant.results = localizationFn("No_Results", "No Results");
    participant.calculatedCoins = 0;
    participant.calculatedXP = 0;
    participant.custom = '';
    participant.badges = [];

    if (isConnect3(gameType) || isConnectAll(gameType)) {
        participant.connect3Metrics = [];
    }
    return participant;
};

export const setParticipantNoActionsHistory = (participant: ParticipantObj) => {
    participant.calculatedCoins = 0;
    participant.calculatedXP = 0;
    participant.badges = [];
    participant.isReverted = false;
    participant.reversionDate = null;
    participant.custom = '';
    return participant;
};

export const setActionItem = (participant: ParticipantObj, actionItem: any) => {
    participant.calculatedCoins += (actionItem.ActionType == 0 || actionItem.ActionType == 6) ? actionItem.NumericValue : 0;
    participant.calculatedXP += (actionItem.ActionType == 1 || actionItem.ActionType == 7) ? actionItem.NumericValue : 0;
    if (actionItem.ActionType == 2 && actionItem.NumericValue) {
        participant.badges.push(actionItem.NumericValue);
    }

    if (actionItem.ActionType == 10 && actionItem.TextValue && actionItem.TextValue.length > 0) { // raffle game
        participant.custom = actionItem.TextValue;
        participant.customRewardValue = actionItem.NumericValue;
        participant.productState = actionItem.NumericValue || -1;
    }

    participant.isReverted = actionItem.Reverted;
    participant.paidDate = actionItem.ActionDate;
    participant.reversionDate = actionItem.RevertedDate;
    return participant;
};

export const setPaymentDate = (
    participant: ParticipantObj,
    localizationFn = defaultLocalization,
    transformDateFn = (value: Date | string | number, format?: string) => value,
    isCxone = false,
    formatDateFn = (date: any, format: string) => date,
) => {
    const separator = isCxone ? ': ' : ' ';
    const reversionDate = isCxone
        ? formatDateFn(participant.reversionDate, CVAngularDateFormat)
        : transformDateFn(participant.reversionDate, CVAngularDateFormat);

    if (participant.isReverted === null || participant.isReverted === undefined) {
        participant.paymentDate = isCxone ? '---' : '';
        return participant;
    }

    if (participant.calculatedCoins == 0 && participant.calculatedXP == 0) {
        participant.paymentDate = "---";
        return participant;
    }

    if (!participant.isReverted) {
        const paidDate = isCxone
            ? formatDateFn(participant.paidDate, CVAngularDateFormat)
            : transformDateFn(participant.paidDate, CVAngularDateFormat);
        participant.paymentDate = `${localizationFn("Paid_on", "Paid on")}${separator}${paidDate}`;
        return participant;
    }

    participant.paymentDate = `${localizationFn("Reverted_on", "Reverted on")}${separator}${reversionDate}`;
    return participant;
};

export const setQualifier = (participant: any, qualifierMetric: IHSelectedMetric) => {
    if (!qualifierMetric) {
        participant.qualifier = '---';
        participant.qualifierValue = 0;
        participant.passed = true; // if there is no qualifier, by default passed = true;
        return participant;
    }
    const qualifierValue = participant.metricsValues?.find(m => m.MetricID == qualifierMetric.MetricID);
    if (!qualifierValue || qualifierValue.MetricDisplayValue === null) {
        participant.qualifier = '---';
        participant.qualifierValue = 0;
        participant.passed = false;
        return participant;
    }
    participant.qualifier = qualifierValue.MetricDisplayValue;
    participant.qualifierValue = qualifierValue.MetricValue;

    participant.passed = isQualified(qualifierMetric, qualifierValue.MetricValue);
    return participant;
};

export const setConnect3Positions = (participant: ParticipantObj, metrics: IHSelectedMetric[]) => {
    const connect3Metrics: MetricValues[] = new Array(9).fill(null);
    const metricCompleted: boolean[] = new Array(9).fill(null);

    if (metrics.length < 9) {
        metricCompleted[4] = true;
        connect3Metrics[4] = {
            MetricID: 0,
            MetricValue: 0,
            MetricDisplayValue: '---',
            metricCompleted: true,
            RelatedID: participant.participant.LoginID,
            MetricValueID: 0
        };
    } //Free Space = true

    //Sort the Positions
    metrics.sort((a, b) => {
        return a.Position - b.Position;
    });

    metrics.forEach((metricPosition) => {
        const metric: MetricValues = participant.metricsValues?.find(m => m.MetricID == metricPosition.MetricID);

        if (metric) {
            metric.metricCompleted = false;
            connect3Metrics[metricPosition.Position] = metric;
        } else {
            connect3Metrics[metricPosition.Position] = {
                MetricID: metricPosition.MetricID,
                MetricValue: 0,
                MetricDisplayValue: '---',
                metricCompleted: false,
                RelatedID: participant.participant.LoginID,
                MetricValueID: 0
            };
        }

        if (metricPosition.MetricID > -1 && metric != null) {
            //match the correct criteria <= for DOWN metrics and >= for UP metrics
            //file games-history-table.component.ts line 456
            metricCompleted[metricPosition.Position] = metricPosition.MetricDetail.DefaultMetricDirection === DefaultDirection.Down ? metric.MetricValue <= metricPosition.LeftNumber : metric.MetricValue >= metricPosition.LeftNumber;
            connect3Metrics[metricPosition.Position].metricCompleted = metricCompleted[metricPosition.Position];
        }
    });
    participant.connect3Metrics = connect3Metrics;
    return participant;
};

export const getJoinStatusIcon = (
    selectedGame: GameObject[],
    alreadyJoined: boolean,
    isCXOne: boolean
): string => {
    if (!selectedGame?.length) return '--';

    const platform = isCXOne ? 'CXOne' : 'PM';
    return alreadyJoined
        ? getStatusIcon('checkMark', platform)
        : getStatusIcon('close', platform)
}

export const getPassedStatusIcon = (
    qualifierMetric: IHSelectedMetric | undefined[] | MetricDetail,
    isPassed: boolean,
    isCXOne: boolean
): string => {
    if (!qualifierMetric) return '--';

    const platform = isCXOne ? 'CXOne' : 'PM';
    return isPassed
        ? getStatusIcon('checkMark', platform)
        : getStatusIcon('close', platform)
}

export const getClassIfNotPinned = (
    isRowPinned: string | undefined | null,
    className: string
): string | string[] => {
    if (isRowPinned) return '';
    return className;
}

export const formatCoin = (
    isCXOne: boolean,
    coin: number
): string => {
    if (isCXOne) {
        return coin === 0 ? '---' : coin.toLocaleString(CVCultureName);
    }
    return `${coin}`;
}

export const connect3AndLevelGamesConnectStatusClass = (
    resultStatus: number,
    isCXOne: boolean
): string => {
    if (!isCXOne) return 'color-cerulean';
    return resultStatus === 1 ? 'player-breakdown__grid-connect-status' : '';
}

export const formatPaymentInfoValue = (
    value: string,
    isCXOne: boolean
): string => {
    return isCXOne && (value === null || value === undefined) ? '---' : value;
}
